// src/components/WalletBackground.js
import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import BaitLogo from '../BaitLogo.png';

const WalletBackground = ({ 
  particleCount = 60,
  particleSize = 120,
  opacity = 0.8,
  rotationSpeed = 0.002,
  movementSpeed = 0.04,
  color = 0x85b2df,
  transparent = true,
  zIndex = 0
}) => {
  const canvasRef = useRef();
  const containerRef = useRef();

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    
    if (!container || !canvas) return;
    
    const width = container.clientWidth;
    const height = container.clientHeight;
    
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 10000);
    const renderer = new THREE.WebGLRenderer({ 
      canvas: canvas,
      alpha: transparent,
      antialias: true
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(color, transparent ? 0 : 1);

    const textureLoader = new THREE.TextureLoader();
    const logoTexture = textureLoader.load(
      BaitLogo,
      () => console.log('Logo loaded successfully'),
      undefined,
      (err) => console.error('Error loading texture:', err)
    );

    const particles = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 2000;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 2000;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 2000;
    }

    particles.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      map: logoTexture,
      size: particleSize,
      transparent: true,
      opacity: opacity,
      blending: THREE.NormalBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });

    const particleSystem = new THREE.Points(particles, material);
    scene.add(particleSystem);

    camera.position.z = 500;

    const animate = () => {
      requestAnimationFrame(animate);

      particleSystem.rotation.x += rotationSpeed;
      particleSystem.rotation.y += rotationSpeed;

      const positions = particleSystem.geometry.attributes.position.array;
      for (let i = 0; i < positions.length; i += 3) {
        positions[i] += (Math.random() - 0.5) * movementSpeed;
        positions[i + 1] += (Math.random() - 0.5) * movementSpeed;
        positions[i + 2] += (Math.random() - 0.5) * movementSpeed;
      }

      particleSystem.geometry.attributes.position.needsUpdate = true;
      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;
      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, newHeight);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
    };

  }, [particleCount, particleSize, opacity, rotationSpeed, movementSpeed, color, transparent]);

  return (
    <div 
      ref={containerRef} 
      className="wallet-bg-container"
      style={{ 
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius: 'inherit',
        zIndex: zIndex,
        pointerEvents: 'none',
      }}
    >
      <canvas 
        ref={canvasRef} 
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
};

export default WalletBackground;